import { computed, inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { forkJoin, of, Observable } from 'rxjs';
import { tap, catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { DocumentTemplateWithPath } from '../types/document-template';
import { Field, FieldCreateDTO, StructureType } from '../types/fields';
import { AnchorTree, AnchorTreeLeafNode } from '../types/anchor-tree';
import { DocumentTemplateService } from '../services/document-template.service';
import { FieldsService } from '../services/fields.service';
import { StructuresService } from '../services/structures.service';
import { AnchorTreeService } from '../services/anchor-tree.service';
import {
  collectPlacedTemplateIds,
  findLeaf,
  findLeafForTemplate,
  getExpectedFieldIdsForLeaf,
} from '../utils/anchor-tree.utils';

export interface OcrPrimeState {
  templates: DocumentTemplateWithPath[];
  templatesLoading: boolean;
  templatesLoaded: boolean;
  templatesError: string | null;

  fields: Field[];
  fieldsLoading: boolean;
  fieldsLoaded: boolean;
  fieldsError: string | null;

  structures: StructureType[];
  structuresLoading: boolean;
  structuresLoaded: boolean;
  structuresError: string | null;

  anchorTree: AnchorTree | null;
  anchorTreeLoading: boolean;
  anchorTreeLoaded: boolean;
  anchorTreeError: string | null;
}

const initialState: OcrPrimeState = {
  templates: [],
  templatesLoading: false,
  templatesLoaded: false,
  templatesError: null,

  fields: [],
  fieldsLoading: false,
  fieldsLoaded: false,
  fieldsError: null,

  structures: [],
  structuresLoading: false,
  structuresLoaded: false,
  structuresError: null,

  anchorTree: null,
  anchorTreeLoading: false,
  anchorTreeLoaded: false,
  anchorTreeError: null,
};

export const OcrPrimeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    templatesMap: computed(() => {
      const map: Record<string, DocumentTemplateWithPath> = {};
      for (const t of state.templates()) {
        map[t.id] = t;
      }
      return map;
    }),
    fieldsMap: computed(() => {
      const map: Record<string, Field> = {};
      for (const f of state.fields()) {
        map[f.id] = f;
      }
      return map;
    }),
    structuresMap: computed(() => {
      const map: Record<string, StructureType> = {};
      for (const s of state.structures()) {
        map[s.id] = s;
      }
      return map;
    }),
    anchorTreeRoot: computed(() => state.anchorTree()?.root ?? null),
    /** Templates not placed on any tree leaf — unreachable for matching. */
    unplacedTemplateIds: computed(() => {
      const placed = collectPlacedTemplateIds(state.anchorTree()?.root ?? null);
      return state
        .templates()
        .map((t) => t.id)
        .filter((id) => !placed.has(id));
    }),
    /** id → Field map (Map instance, for the anchor-status utils). */
    fieldsByIdMap: computed(() => {
      const map = new Map<string, Field>();
      for (const f of state.fields()) {
        map.set(f.id, f);
      }
      return map;
    }),
  })),
  withMethods((store) => {
    const templateService = inject(DocumentTemplateService);
    const fieldsService = inject(FieldsService);
    const structuresService = inject(StructuresService);
    const anchorTreeService = inject(AnchorTreeService);

    const loadAnchorTreeInternal = (forceRefresh: boolean): void => {
      if (store.anchorTreeLoaded() && !forceRefresh) return;
      patchState(store, { anchorTreeLoading: true, anchorTreeError: null });
      const source$ = forceRefresh
        ? anchorTreeService.refreshAnchorTreeCache()
        : anchorTreeService.getAnchorTree();
      source$.subscribe({
        next: (anchorTree) =>
          patchState(store, {
            anchorTree,
            anchorTreeLoading: false,
            anchorTreeLoaded: true,
            anchorTreeError: null,
          }),
        error: (err) =>
          patchState(store, {
            anchorTreeLoading: false,
            anchorTreeError: err?.message || 'Failed to load anchor tree',
          }),
      });
    };

    return {
      // ── Templates ──────────────────────────────────────────────
      loadTemplates(forceRefresh = false): void {
        if (store.templatesLoaded() && !forceRefresh) return;
        patchState(store, { templatesLoading: true, templatesError: null });
        const source$ = forceRefresh
          ? templateService.refreshTemplateCache()
          : templateService.getDocumentTemplates();
        source$.subscribe({
          next: (templates) =>
            patchState(store, {
              templates,
              templatesLoading: false,
              templatesLoaded: true,
              templatesError: null,
            }),
          error: (err) =>
            patchState(store, {
              templatesLoading: false,
              templatesError: err?.message || 'Failed to load templates',
            }),
        });
      },

      refreshTemplates(): void {
        patchState(store, { templatesLoading: true, templatesError: null });
        templateService.refreshTemplateCache().subscribe({
          next: (templates) =>
            patchState(store, {
              templates,
              templatesLoading: false,
              templatesLoaded: true,
              templatesError: null,
            }),
          error: (err) =>
            patchState(store, {
              templatesLoading: false,
              templatesError: err?.message || 'Failed to refresh templates',
            }),
        });
      },

      getTemplateById(id: string): DocumentTemplateWithPath | undefined {
        return store.templatesMap()[id];
      },

      removeTemplateLocally(id: string): void {
        patchState(store, {
          templates: store.templates().filter((t) => t.id !== id),
        });
      },

      // ── Fields ─────────────────────────────────────────────────
      loadFields(forceRefresh = false): Observable<Field[]> {
        if (store.fieldsLoaded() && !forceRefresh) return of(store.fields());
        patchState(store, { fieldsLoading: true, fieldsError: null });
        const source$ = forceRefresh
          ? fieldsService.refreshFieldsCache()
          : fieldsService.getFields();
        const shared$ = source$.pipe(
          tap((fields) =>
            patchState(store, {
              fields,
              fieldsLoading: false,
              fieldsLoaded: true,
              fieldsError: null,
            })
          ),
          catchError((err) => {
            patchState(store, {
              fieldsLoading: false,
              fieldsError: err?.message || 'Failed to load fields',
            });
            return of([] as Field[]);
          }),
          shareReplay(1)
        );
        // Auto-subscribe so existing void callers still trigger the request.
        shared$.subscribe();
        return shared$;
      },

      refreshFields(): void {
        patchState(store, { fieldsLoading: true, fieldsError: null });
        fieldsService.refreshFieldsCache().subscribe({
          next: (fields) =>
            patchState(store, {
              fields,
              fieldsLoading: false,
              fieldsLoaded: true,
              fieldsError: null,
            }),
          error: (err) =>
            patchState(store, {
              fieldsLoading: false,
              fieldsError: err?.message || 'Failed to refresh fields',
            }),
        });
      },

      getFieldById(id: string): Field | undefined {
        return store.fieldsMap()[id];
      },

      removeFieldLocally(id: string): void {
        patchState(store, {
          fields: store.fields().filter((f) => f.id !== id),
        });
      },

      /**
       * Update a field globally: call the service, then refresh the store cache
       * so all consumers re-render with the latest field metadata. The returned
       * Observable emits the refreshed fields list once the store is updated.
       */
      updateField(id: string, data: Partial<FieldCreateDTO>): Observable<Field[]> {
        return fieldsService.updateField(id, data).pipe(
          switchMap(() => {
            // Force-refresh the store from the (now-invalidated) service cache.
            patchState(store, { fieldsLoading: true, fieldsError: null });
            return fieldsService.refreshFieldsCache().pipe(
              tap((fields) =>
                patchState(store, {
                  fields,
                  fieldsLoading: false,
                  fieldsLoaded: true,
                  fieldsError: null,
                })
              ),
              catchError((err) => {
                patchState(store, {
                  fieldsLoading: false,
                  fieldsError: err?.message || 'Failed to refresh fields',
                });
                return of(store.fields());
              })
            );
          }),
          shareReplay(1)
        );
      },

      /** Return fields belonging to a given structure */
      getFieldsForStructure(structureId: string): Field[] {
        const structure = store.structuresMap()[structureId];
        if (!structure) return [];
        const idSet = new Set(structure.fieldIds);
        return store.fields().filter((f) => idSet.has(f.id));
      },

      /** Return a name→Field map for a given structure */
      getFieldsMapForStructure(structureId: string): Record<string, Field> {
        const structure = store.structuresMap()[structureId];
        if (!structure) return {};
        const idSet = new Set(structure.fieldIds);
        const result: Record<string, Field> = {};
        let collisionWarned = false;
        for (const f of store.fields()) {
          if (idSet.has(f.id)) {
            if (!collisionWarned && result[f.name]) {
              console.warn(
                `Structure "${structure.name || structureId}" has duplicate field name "${f.name}". Field-name uniqueness within a structure is required.`
              );
              collisionWarned = true;
            }
            result[f.name] = f;
          }
        }
        return result;
      },

      // ── Structures ─────────────────────────────────────────────
      loadStructures(forceRefresh = false): void {
        if (store.structuresLoaded() && !forceRefresh) return;
        patchState(store, { structuresLoading: true, structuresError: null });
        const source$ = forceRefresh
          ? structuresService.refreshStructuresCache()
          : structuresService.getStructures();
        source$.subscribe({
          next: (structures) =>
            patchState(store, {
              structures,
              structuresLoading: false,
              structuresLoaded: true,
              structuresError: null,
            }),
          error: (err) =>
            patchState(store, {
              structuresLoading: false,
              structuresError: err?.message || 'Failed to load structures',
            }),
        });
      },

      refreshStructures(): void {
        patchState(store, { structuresLoading: true, structuresError: null });
        structuresService.refreshStructuresCache().subscribe({
          next: (structures) =>
            patchState(store, {
              structures,
              structuresLoading: false,
              structuresLoaded: true,
              structuresError: null,
            }),
          error: (err) =>
            patchState(store, {
              structuresLoading: false,
              structuresError: err?.message || 'Failed to refresh structures',
            }),
        });
      },

      getStructureById(id: string): StructureType | undefined {
        return store.structuresMap()[id];
      },

      removeStructureLocally(id: string): void {
        patchState(store, {
          structures: store.structures().filter((s) => s.id !== id),
        });
      },

      // ── Anchor tree ────────────────────────────────────────────
      loadAnchorTree(forceRefresh = false): void {
        loadAnchorTreeInternal(forceRefresh);
      },

      refreshAnchorTree(): void {
        loadAnchorTreeInternal(true);
      },

      /** Finds the leaf a template is placed on (tree = source of truth). */
      leafForTemplate(templateId: string): AnchorTreeLeafNode | null {
        return findLeafForTemplate(store.anchorTree()?.root ?? null, templateId);
      },

      /** Finds a leaf by node id. */
      leafById(leafNodeId: string): AnchorTreeLeafNode | null {
        return findLeaf(store.anchorTree()?.root ?? null, leafNodeId);
      },

      /** Expected anchor set for a leaf = path decisive anchors ∪ leaf anchors. */
      expectedAnchorFieldIdsForLeaf(leafNodeId: string): string[] {
        return getExpectedFieldIdsForLeaf(store.anchorTree()?.root ?? null, leafNodeId);
      },

      /**
       * Load all platform data (templates, fields, structures) in parallel.
       * Returns an Observable<boolean> that completes when all data is loaded.
       * Skips already-loaded resources.
       */
      loadAllPlatformData(): Observable<boolean> {
        if (
          store.templatesLoaded() &&
          store.fieldsLoaded() &&
          store.structuresLoaded() &&
          store.anchorTreeLoaded()
        ) {
          return of(true);
        }

        patchState(store, {
          templatesLoading: !store.templatesLoaded(),
          fieldsLoading: !store.fieldsLoaded(),
          structuresLoading: !store.structuresLoaded(),
          anchorTreeLoading: !store.anchorTreeLoaded(),
          templatesError: null,
          fieldsError: null,
          structuresError: null,
          anchorTreeError: null,
        });

        return forkJoin({
          templates: store.templatesLoaded()
            ? of(store.templates())
            : templateService.getDocumentTemplates().pipe(
                catchError(() => of([] as any[]))
              ),
          fields: store.fieldsLoaded()
            ? of(store.fields())
            : fieldsService.getFields().pipe(
                catchError(() => of([] as any[]))
              ),
          structures: store.structuresLoaded()
            ? of(store.structures())
            : structuresService.getStructures().pipe(
                catchError(() => of([] as any[]))
              ),
          anchorTree: store.anchorTreeLoaded()
            ? of(store.anchorTree())
            : anchorTreeService.getAnchorTree().pipe(
                catchError(() => of(null))
              ),
        }).pipe(
          tap(({ templates, fields, structures, anchorTree }) => {
            if (!store.templatesLoaded()) {
              patchState(store, {
                templates,
                templatesLoading: false,
                templatesLoaded: true,
              });
            }
            if (!store.fieldsLoaded()) {
              patchState(store, {
                fields,
                fieldsLoading: false,
                fieldsLoaded: true,
              });
            }
            if (!store.structuresLoaded()) {
              patchState(store, {
                structures,
                structuresLoading: false,
                structuresLoaded: true,
              });
            }
            if (!store.anchorTreeLoaded()) {
              patchState(store, {
                anchorTree,
                anchorTreeLoading: false,
                anchorTreeLoaded: true,
              });
            }
          }),
          map(() => true),
          catchError(() => of(true))
        );
      },
    };
  })
);
